import React, { useState } from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    Button,
    InputLabel,

    CircularProgress,

    Checkbox,
    ListItemText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import theme from '../../theme/theme';
import { useStationsContext } from '../../context/StationsContext';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: theme.palette.primary.dark,
        backgroundColor: theme.palette.background.default,
        borderRadius: '8px',
        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
        '&:hover fieldset': { borderColor: theme.palette.primary.light },
        '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
    },
    '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-main)' }
};

const FilterPanel = ({ onFetch, loading }) => {
    const { stations, loadingStations, getStationName } = useStationsContext();

    // Station is always an array for consistent multi-select behavior
    const [station, setStation] = useState([]);
    const [trendType, setTrendType] = useState('Daily');

    const [date, setDate] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const isFormValid =
        station.length > 0 &&
        (
            (trendType === 'Daily' && date !== null) ||
            ((trendType === 'Range' || trendType === 'Weekly') && fromDate !== null && toDate !== null)
        );

    const handleFetch = () => {
        if (!onFetch) return;

        // Convert station IDs to station names for API
        const stationNames = station.map(id => getStationName(id));

        if (trendType === 'Daily') {
            onFetch({
                station: stationNames,
                trendType,
                date: dayjs(date).format('YYYY-MM-DD')
            });
        }

        if (trendType === 'Range' || trendType === 'Weekly') {
            onFetch({
                station: stationNames,
                trendType,
                fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
                toDate: dayjs(toDate).format('YYYY-MM-DD')
            });
        }
    };

    const handleTrendTypeChange = (e) => {
        const newTrendType = e.target.value;
        setTrendType(newTrendType);

        // BUSINESS RULE: When Trend Type = Date Range or Weekly -> Force Single Station
        if (newTrendType === 'Range' || newTrendType === 'Weekly') {
            // Logic handled by array length check in parent content or handleStationChange?
            // Actually I should verify if stationMode variable usage is incorrectly left in Logic above.
            // Previous view showed:     // BUSINESS RULE: When Trend Type = Date Range or Weekly -> Force Single Station
            // if (newTrendType === 'Range' || newTrendType === 'Weekly') {
            //    setStationMode('single'); <--- THIS IS UNDEFINED!

            // I need to REMOVE setStationMode usage.
            if (station.length > 1) {
                setStation([station[0]]);
            }
        }

        setDate(null);
        setFromDate(null);
        setToDate(null);
    };

    // Removed handleStationModeChange as it used setStationMode

    const handleStationChange = (event) => {
        const { value } = event.target;
        // value is an array of strings for multiple select
        setStation(typeof value === 'string' ? value.split(',') : value);
    };

    return (
        <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, 1fr)',
                        gap: '1.5rem',
                        alignItems: 'start'
                    }}
                >
                    {/* Trend Type - Moved to First Column */}
                    <div style={{ gridColumn: 'span 3', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <FormControl fullWidth size="small" sx={inputSx}>
                            <InputLabel>Trend Type</InputLabel>
                            <Select
                                value={trendType}
                                onChange={handleTrendTypeChange}
                                label="Trend Type"
                            >
                                <MenuItem value="Daily">Daily</MenuItem>
                                <MenuItem value="Weekly">Weekly</MenuItem>
                                <MenuItem value="Range">Date Range</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    {/* Station Select - Moved to Second Column */}
                    <div style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <FormControl fullWidth size="small" sx={inputSx}>
                            <InputLabel>Select Station</InputLabel>
                            <Select
                                multiple
                                value={station}
                                onChange={handleStationChange}
                                label="Select Station"
                                renderValue={(selected) => selected && selected.length > 0 ? selected.map(id => getStationName(id)).join(', ') : 'Choose stations...'}
                            >
                                {loadingStations ? (
                                    <MenuItem disabled>
                                        <ListItemText primary="Loading stations..." />
                                    </MenuItem>
                                ) : (stations && stations.length > 0) ? (
                                    stations.map((stationId) => (
                                        <MenuItem key={stationId} value={stationId}>
                                            <Checkbox checked={station.indexOf(stationId) > -1} size="small" />
                                            <ListItemText primary={getStationName(stationId)} />
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>
                                        <ListItemText primary="No stations available" />
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Date Picker(s) */}
                    <div style={{ gridColumn: 'span 3' }}>
                        {trendType === 'Daily' ? (
                            <DatePicker
                                label="Select Date"
                                value={date}
                                onChange={setDate}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        sx: inputSx
                                    }
                                }}
                            />
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        onChange={(newDate) => {
                                            setFromDate(newDate);
                                            // BUSINESS RULE: Weekly -> Auto-set To Date to +6 days
                                            if (trendType === 'Weekly' && newDate) {
                                                setToDate(dayjs(newDate).add(6, 'day'));
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                sx: inputSx
                                            }
                                        }}
                                    />

                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        onChange={setToDate}
                                        minDate={fromDate}
                                        disabled={trendType === 'Weekly'}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                sx: inputSx
                                            }
                                        }}
                                    />
                                </div>
                                {trendType === 'Weekly' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        marginTop: '8px',
                                        backgroundColor: '#e3f2fd', // Light blue highlight
                                        color: '#0d47a1',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        border: '1px solid #bbdefb',
                                        whiteSpace: 'nowrap',
                                        zIndex: 10,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <span>📅</span> Weekly trend displays 7 days starting from selected date
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Fetch Button */}
                    <div style={{ gridColumn: 'span 2' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleFetch}
                            disabled={!isFormValid || loading}
                            sx={{
                                height: '40px',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '8px',
                                bgcolor: 'var(--secondary-main)',
                                '&:hover': { bgcolor: 'var(--secondary-dark)' }
                            }}
                        >
                            {loading ? <CircularProgress size={20} /> : 'View Trend'}
                        </Button>
                    </div>
                </div>
            </LocalizationProvider>
        </div>
    );
};

export default FilterPanel;
